/**
 * Performance utilities.
 *
 * deepFreeze: congela um objeto/array e tudo abaixo dele de forma recursiva.
 *
 * Why: no Vue 2, atribuir um objeto a uma property reativa do `data()`
 * dispara `Vue.observable`, que percorre RECURSIVAMENTE todas as keys e
 * cria um par getter/setter + um Dep para cada propriedade. Em arrays
 * grandes (ex. response de comissoes com centenas de items x dezenas de
 * campos), isso gera dezenas/centenas de milhares de Dep/Observer
 * mantidos em memoria enquanto a referencia estiver viva.
 *
 * Para dados que sao "read-only para display" (substituidos por
 * referencia quando o filtro muda, NUNCA mutados in-place), congelar
 * antes de atribuir faz o Vue PULAR o defineReactive recursivo — o
 * objeto raiz continua reativo (o setter da chave dispara watchers),
 * mas as propriedades internas nao viram reactive accessors.
 *
 * Trade-off: tentar mutar in-place (`obj.foo = 'bar'`) vai falhar
 * silenciosamente em sloppy mode ou lancar TypeError em strict mode.
 * Use apenas em dados que serao SUBSTITUIDOS, nunca MUTADOS.
 */
export function deepFreeze(obj) {
  if (obj === null || typeof obj !== 'object' || Object.isFrozen(obj)) {
    return obj
  }
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      deepFreeze(obj[i])
    }
  } else {
    const keys = Object.keys(obj)
    for (const key of keys) {
      deepFreeze(obj[key])
    }
  }
  return Object.freeze(obj)
}

/**
 * freezeItems: congela cada ITEM da coleção (deepFreeze), mas NÃO o
 * container (array ou object map).
 *
 * Why: nos stores, o padrão ADD_* faz upsert com push no array do state e
 * mutations gravam novas keys em maps por id. Congelar o container
 * quebraria esses fluxos (push em array frozen lança TypeError). Congelar
 * só os itens mantém a reatividade de LISTA (push/replace disparam
 * watchers) e corta a parte cara: o Vue pula o defineReactive recursivo
 * dos N itens × M campos, pois `observe()` ignora objetos não-extensíveis.
 *
 * Uso (mutation):
 *   state.clientAssets.data = freezeItems(payload)
 *
 * Mesmo trade-off do deepFreeze: itens são read-only — substitua o item
 * inteiro (splice/push), nunca mute campos in-place.
 */
export function freezeItems(collection) {
  if (collection === null || typeof collection !== 'object') {
    return collection
  }
  if (Array.isArray(collection)) {
    for (const item of collection) {
      deepFreeze(item)
    }
    return collection
  }
  for (const key of Object.keys(collection)) {
    deepFreeze(collection[key])
  }
  return collection
}

/**
 * upsertFrozenById: substitui o padrão de upsert `old[key] = data[key]`
 * (mutação in-place do item já no state) por copy-on-write congelado.
 *
 * Why: o padrão antigo mutava campos de um item já existente no array. Com
 * itens congelados (freezeItems) isso lança TypeError. Este helper preserva
 * a MESMA semântica de merge parcial (mantém campos do item antigo que não
 * vêm no payload) mas produzindo um objeto NOVO e congelado, trocado no
 * array via splice (rastreado pela reatividade do Vue) — em vez de mutar o
 * item existente. O container (array) permanece mutável.
 *
 * Clona o payload defensivamente (isola o store de mutações externas do
 * payload), como as mutations ADD_* já faziam. structuredClone é o padrão do
 * codebase (consolidator/fee-fixo) e seguro para dados vindos de API (JSON).
 *
 * @param {Array} list  o array reativo do state (ex.: state.commissions.data)
 * @param {Object|Array} payload  item ou lista de itens com `id`
 */
export function upsertFrozenById(list, payload) {
  if (!Array.isArray(list) || payload === null || typeof payload !== 'object') {
    return list
  }
  let dados = structuredClone(payload)
  if (!Array.isArray(dados)) {
    dados = [dados]
  }
  dados.forEach((data) => {
    const idx = list.findIndex((item) => item.id === data.id)
    if (idx === -1) {
      list.push(deepFreeze(data))
    } else {
      list.splice(idx, 1, deepFreeze({ ...list[idx], ...data }))
    }
  })
  return list
}
